import {
  Card,
  Stack,
  Box,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TablePagination,
  Chip,
  Pagination,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { useHistory } from "react-router-dom";
import { useState, useEffect } from "react";
import ResultsTable from "./Table";
import DownloadVendor from "./DownloadVendor";
import Api from "../../service/api";
import ResultCards from "./ResultCards";
import BackButton from "../bulk-operations/BackButton";

const SearchResults = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [continuePolling, setContinuePolling] = useState(true);
  const [abortController, setAbortController] = useState(null);
  const [filterResults, setFilterResults] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [pollingStartTime, setPollingStartTime] = useState(null);
  const [pollingTimeRemaining, setPollingTimeRemaining] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [oneKeyId, setOneKeyId] = useState("");
  const [textType, setTextType] = useState("confirmation");
  const uploadIdSession = sessionStorage.getItem("searchUploadId");
  const serachIn = sessionStorage.getItem("searchIn");
  const totalResults = sessionStorage.getItem("totalCsvRows");
  const searchType = sessionStorage.getItem("searchType");
  const [exportLoading, setExportLoading] = useState(false);

  const maxTimePoling = 30 * 60; // 30 minutes in seconds

  const CustomButton = styled(Button)(({ theme }) => ({
    borderColor: "#D31710",
    color: "#D31710",
    "&:hover": {
      backgroundColor: "#FBF5F4",
      borderColor: "#B01309",
      color: "#B01309",
    },
    "&:disabled": {
      backgroundColor: theme.palette.grey[300],
      borderColor: theme.palette.grey[300],
      color: theme.palette.grey[500],
    },
  }));

  const [pageStates, setPageStates] = useState({
    foundInEPH: { page: 0, rowsPerPage: 5 },
    foundInVendor: { page: 0, rowsPerPage: 5 },
    notFound: { page: 0, rowsPerPage: 5 },
  });

  const [accordionPage, setAccordionPage] = useState(0);
  const [accordionRowsPerPage, setAccordionRowsPerPage] = useState(10);
  const [expandedAccordions, setExpandedAccordions] = useState({});

  const handleChangePage = (category, newPage) => {
    setPageStates((prev) => ({
      ...prev,
      [category]: { ...prev[category], page: newPage },
    }));
  };

  const handleChangeRowsPerPage = (category, event) => {
    setPageStates((prev) => ({
      ...prev,
      [category]: { page: 0, rowsPerPage: parseInt(event.target.value, 10) },
    }));
  };

  const handleAccordionPageChange = (event, newPage) => {
    setAccordionPage(newPage);
  };

  const handleAccordionRowsPerPageChange = (event) => {
    setAccordionRowsPerPage(parseInt(event.target.value, 10));
    setAccordionPage(0);
  };
  const handleAccordionExpand =
    (accordionId, hasMatches) => (event, isExpanded) => {
      if (!hasMatches && isExpanded) {
        return;
      }

      setExpandedAccordions((prev) => ({
        ...prev,
        [accordionId]: isExpanded,
      }));
    };

  const headers =
    serachIn === "eph"
      ? ["Enterprise ID", "Name", "Country", "Status", "Actions"]
      : serachIn === "vendor"
        ? ["Vendor Unique ID", "Name", "Country", "Status", "Actions"]
        : ["Enterprise ID", "Vendor Unique ID", "Status", "Actions"];
  const filters =
    serachIn === "eph"
      ? [
          { label: "All", value: "All" },
          { label: "Found in EPH", value: "Found in EPH" },
          { label: "Not Found", value: "Not Found" },
        ]
      : serachIn === "vendor"
        ? [
            { label: "All", value: "All" },
            { label: "Found in Vendor", value: "Found in Vendor" },
            { label: "Not Found", value: "Not Found" },
          ]
        : [
            { label: "All", value: "All" },
            { label: "Found in EPH", value: "Found in EPH" },
            { label: "Found in Vendor", value: "Found in Vendor" },
            { label: "Not Found", value: "Not Found" },
          ];

  useEffect(() => {
    if (!searchKeyword.trim()) {
      setFilteredResults(filterResults);
    } else {
      const filtered = filterResults.filter((result) => {
        // Search in input criteria
        const inputMatch =
          result.input &&
          result.input.some((inputObj) =>
            Object.values(inputObj).some(
              (value) =>
                value &&
                value
                  .toString()
                  .toLowerCase()
                  .includes(searchKeyword.toLowerCase()),
            ),
          );

        return inputMatch;
      });
      setFilteredResults(filtered);
    }
  }, [searchKeyword, filterResults]);
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);
  useEffect(() => {
    const sleep = (ms, signal) =>
      new Promise((resolve, reject) => {
        const timeout = setTimeout(resolve, ms);
        if (signal) {
          signal.addEventListener("abort", () => {
            clearTimeout(timeout);
            reject(new Error("Sleep was cancelled"));
          });
        }
      });
    const shouldContinuePollingByTime = (startTime) => {
      if (searchType !== "advance" || !startTime) return true;
      const elapsed = Date.now() - startTime;
      const maxPollingTime = maxTimePoling * 1000;
      return elapsed < maxPollingTime;
    };
    const fetchResults = async () => {
      if (!serachIn || !uploadIdSession) {
        history.push("/upload-history");
        return;
      }
      const uploadId = uploadIdSession;
      if (!uploadId) {
        console.error("No uploadId found in location state");
        return;
      }
      const controller = new AbortController();
      setAbortController(controller);
      const signal = controller.signal;
      setIsLoading(true);
      const startTime = Date.now();
      if (searchType === "advance") {
        setPollingStartTime(startTime);
        setPollingTimeRemaining(maxTimePoling); // 20 minutes in seconds
      }
      let currentIndex = 0;
      let allResults = [];
      let shouldContinue = true;
      while (shouldContinue && !signal.aborted) {
        try {
          if (signal.aborted) {
            break;
          }
          if (
            searchType === "advance" &&
            !shouldContinuePollingByTime(startTime)
          ) {
            setContinuePolling(false);
            shouldContinue = false;
            break;
          }
          const response = await fetchWithRetry(uploadId, currentIndex, signal);
          if (!response || !response.data) {
            shouldContinue = false;
            setContinuePolling(false);
            break;
          }
          currentIndex++;
          const newData = Array.isArray(response.data)
            ? response.data
            : response.data.results || [];
          const continuePolling = response.data.continuePolling === true;

          if (newData.length > 0) {
            setIsLoading(false);
          }
          if (continuePolling && newData.length === 0) {
            if (signal.aborted) break;
            const waitTime = searchType === "advance" ? 20000 : 5000;
            await sleep(waitTime, signal);
            if (signal.aborted) break;
            if (
              searchType === "advance" &&
              !shouldContinuePollingByTime(startTime)
            ) {
              setContinuePolling(false);
              shouldContinue = false;
              break;
            }
            const retryResponse = await fetchWithRetry(
              uploadId,
              currentIndex,
              signal,
            );
            const retryData = Array.isArray(retryResponse?.data)
              ? retryResponse.data
              : retryResponse?.data?.results || [];
            if (retryData.length === 0) {
              if (signal.aborted) break;
              const finalWaitTime = searchType === "advance" ? 20000 : 20000;
              await sleep(finalWaitTime, signal);
              if (signal.aborted) break;
              if (
                searchType === "advance" &&
                !shouldContinuePollingByTime(startTime)
              ) {
                setContinuePolling(false);
                shouldContinue = false;
                break;
              }
              const finalResponse = await fetchWithRetry(
                uploadId,
                currentIndex,
                signal,
              );
              const finalData = Array.isArray(finalResponse?.data)
                ? finalResponse.data
                : finalResponse?.data?.results || [];
              if (finalData.length === 0 && searchType === "basic") {
                shouldContinue = false;
                setContinuePolling(false);
                break;
              }
              allResults = [...allResults, ...finalData];
              setResults(allResults);
              if (searchType === "advance") {
                setFilterResults(allResults || []);
              }
              currentIndex++;
              continue;
            }
            allResults = [...allResults, ...retryData];
            setResults(allResults);
            if (searchType === "advance") {
              setFilterResults(allResults || []);
            }
            currentIndex++;
            continue;
          }
          allResults = [...allResults, ...newData];
          setResults(allResults);
          if (searchType === "advance") {
            setFilterResults(allResults || []);
          }
          if (response.data.continuePolling === false) {
            setContinuePolling(false);
            shouldContinue = false;
            break;
          }
          currentIndex++;
        } catch (error) {
          if (
            error.name === "AbortError" ||
            error.message.includes("cancelled")
          ) {
            console.log("Polling was cancelled by user");
          } else {
            console.error(
              `Error fetching results at index ${currentIndex}:`,
              error,
            );
          }
          shouldContinue = false;
        }
      }
      setIsLoading(false);
      setAbortController(null);
      if (searchType === "advance") {
        setPollingStartTime(null);
        setPollingTimeRemaining(null);
      }
    };
    const fetchWithRetry = async (uploadId, index, signal, retryCount = 0) => {
      const MAX_RETRIES = 3;
      try {
        if (signal?.aborted) {
          throw new Error("Fetch operation was cancelled");
        }
        let data = {
          upload_id: uploadId,
          session: index === 0 ? "0" : "1",
          searchType: searchType,
        };
        const response = await Api.fetchBulkSearchResults(data, signal);
        return response;
      } catch (error) {
        if (retryCount < MAX_RETRIES && !signal?.aborted) {
          console.log(`Retrying fetch for index ${index} after 5 seconds...`);
          await sleep(5000, signal);
          return fetchWithRetry(uploadId, index, signal, retryCount + 1);
        } else {
          throw error;
        }
      }
    };

    fetchResults();
  }, []);

  useEffect(() => {
    let intervalId;
    if (searchType === "advance" && pollingStartTime && continuePolling) {
      intervalId = setInterval(() => {
        const elapsed = Math.floor((Date.now() - pollingStartTime) / 1000);
        const maxTime = maxTimePoling; // 20 minutes in seconds
        const remaining = Math.max(0, maxTime - elapsed);
        setPollingTimeRemaining(remaining);
        if (remaining === 0) {
          setContinuePolling(false);
          clearInterval(intervalId);
        }
      }, 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [searchType, pollingStartTime, continuePolling]);

  const [vendorFname, setVendorFname] = useState("");
  const [vendorLname, setVendorLname] = useState("");
  const [vendorCountry, setVendorCountry] = useState("");
  const [hcoName, setName] = useState("");
  const [vendorType, setVendorType] = useState("");
  const [vendorResponse, setResponseVendor] = useState("");
  const [recordType, setType] = useState("");

  const handleDownload = (result) => {
    setTextType("confirmation");
    setType(result.type);
    setDownloadModalOpen(true);
    setOneKeyId(
      result.type === "HCO" ? result.HCOUniqueId : result.HCPUniqueId,
    );
    if (result.type === "HCO") {
      setName(result.Name);
    } else {
      setVendorFname(result.FirstName);
      setVendorLname(result.LastName);
    }
    setVendorCountry(result.CountryCode);
    setVendorType(result.type);
  };
  const handleConfirm = () => {
    setDownloadModalOpen(false);
    setIsLoading(true);
    let payload;
    if (vendorType === "HCO") {
      payload = {
        vendor: "IQVIA",
        onekeyEid: oneKeyId,
        country: vendorCountry,
        name: hcoName,
      };
    } else {
      payload = {
        vendor: "IQVIA",
        onekeyEid: oneKeyId,
        country: vendorCountry,
        firstName: vendorFname,
        lastName: vendorLname,
      };
    }

    Api.subscribeProfile(payload)
      .then((response) => {
        setTextType(response?.data?.eph_profile_flag ? "available" : "success");
        setDownloadModalOpen(true);
        setIsLoading(false);
        setResponseVendor(response?.data);
        //setOneKeyId("");
      })
      .catch((error) => {
        setTextType("error");
        setDownloadModalOpen(true);
        setIsLoading(false);
        //setOneKeyId("");
      });
  };
  const TablePaginationActions = (props) => {
    const { count, page, rowsPerPage, onPageChange } = props;
    const pageCount = Math.ceil(count / rowsPerPage);
    const handleChange = (event, value) => {
      onPageChange(event, value - 1);
    };

    return (
      <Box sx={{ flexShrink: 0, ml: 2.5 }}>
        <Pagination
          count={pageCount}
          page={page + 1}
          onChange={handleChange}
          color="error"
          shape="rounded"
        />
      </Box>
    );
  };
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchKeyword(value);
    setAccordionPage(0);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
  };

  const sortResults = (results) => {
    if (!sortOrder) return results;

    return [...results].sort((a, b) => {
      const aValue = a.results.length;
      const bValue = b.results.length;

      if (sortOrder === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const handleExportAll = async () => {
    setExportLoading(true);

    // Pre-compute formatted search criteria to avoid repetitive string operations
    const searchCriteriaCache = new Map();

    const getFormattedSearchCriteria = (input) => {
      const cacheKey = JSON.stringify(input);
      if (searchCriteriaCache.has(cacheKey)) {
        return searchCriteriaCache.get(cacheKey);
      }

      const formatted = Object.entries(input || {})
        .filter(([key, value]) => value)
        .map(([key, value]) => {
          const formattedKey = key
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/[_-]/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ");
          return `${formattedKey}: ${value}`;
        })
        .join(", ");

      searchCriteriaCache.set(cacheKey, formatted);
      return formatted;
    };

    // Helper function to escape CSV values
    const escapeCsvValue = (value) => {
      const stringValue = String(value || "");
      // If the value contains comma, newline, or quotes, wrap in quotes and escape existing quotes
      if (
        stringValue.includes(",") ||
        stringValue.includes("\n") ||
        stringValue.includes('"')
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Create export data with optimized flat mapping - include all results (both matches and no matches)
    const exportData = filterResults.flatMap((result) => {
      const inputData = result.input[0] || {};

      // Create search criteria columns with formatted headers and prefix to avoid conflicts
      const searchCriteriaColumns = {};
      Object.entries(inputData).forEach(([key, value]) => {
        if (value) {
          const formattedKey = key
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/[_-]/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ");
          // Add "Input " prefix to avoid conflicts with result columns
          searchCriteriaColumns[`Input ${formattedKey}`] = value;
        }
      });
      // If no matches found, still create a row with search criteria
      if (result.results.length === 0) {
        return [
          {
            ...searchCriteriaColumns,
            "Entity ID": "",
            Name: "",
            HCPEnterpriseId: "",
            HCOEnterpriseId: "",
            HCPReferenceId: "",
            HCOReferenceId: "",
            "Country Code": "",
            "Active Flag": "",
            "Classification Code": "",
            "Primary Specialty": "",
            "Primary Address": "",
            Type: "",
            Result: "No Match",
          },
        ];
      }

      return result.results.map((match) => ({
        ...searchCriteriaColumns,
        "Entity ID": match.EntityId || "",
        Name:
          `${match.FirstName || ""} ${match.LastName || ""}`.trim() ||
          match.Name ||
          "",
        HCPEnterpriseId: match.HCPUniqueId || "",
        HCOEnterpriseId: match.HCOUniqueId || "",
        HCPReferenceId: match.HCPReferenceId || "",
        HCOReferenceId: match.HCOReferenceId || "",
        Country: match.CountryCode || "",
        "Active Flag": match.ActiveFlag || "",
        "Classification Code": match.ClassificationCode || "",
        "Primary Specialty": Array.isArray(match.Specialities)
          ? match.Specialities.join(", ")
          : match.Specialities || "",
        "Primary Address": match.Address || "",
        // "First Name": match.FirstName || "",
        // "Last Name": match.LastName || "",
        Type: match.type || "",
        Result: match.Source || "",
      }));
    });

    if (exportData.length === 0) {
      alert("No data to export");
      setExportLoading(false);
      return;
    }

    // Create CSV content
    const headers = Object.keys(exportData[0]);

    // Create header row
    const csvHeader = headers.map((header) => escapeCsvValue(header)).join(",");

    // Create data rows
    const csvRows = exportData.map((row) =>
      headers.map((header) => escapeCsvValue(row[header])).join(","),
    );

    // Combine header and data with UTF-8 BOM for proper character encoding
    const csvContent = "\uFEFF" + [csvHeader, ...csvRows].join("\n");

    // Create and download CSV file with UTF-8 encoding
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `bulk_search_results_${new Date().toISOString().split("T")[0]}.csv`;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setExportLoading(false);
  };
  return (
    <div className="container body-height extra-margin-top-less mb-4">
      {isLoading && results.length === 0 && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: "#ce2a1d" }} size={64} />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.125rem",
                color: "#ce2a1d",
              }}
            >
              Loading results...
            </Typography>
          </Box>
        </Box>
      )}
      <Card sx={{ maxWidth: 1200 }} className="mx-auto">
        {searchType === "advance" ? (
          <Box className="mx-5 my-4">
            <ResultCards
              totalRecords={totalResults}
              searchType={searchType}
              totalFound={
                filteredResults.length -
                filterResults.filter((result) => result.results.length === 0)
                  .length
              }
              totalNotFound={
                filterResults.filter((result) => result.results.length === 0)
                  .length
              }
              totalFailed={0}
            />
            {continuePolling && pollingTimeRemaining !== null && (
              <Box>
                <Typography
                  variant="h6"
                  className="upload-phase-text"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Loading results...
                </Typography>
                <LinearProgress
                  style={{
                    color: "rgb(185, 28, 28)",
                  }}
                  color="inherit"
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
              style={{ marginBottom: "15px", marginTop: "15px" }}
            >
              <TextField
                style={{ width: "61%" }}
                className="input-search-bs"
                variant="outlined"
                placeholder="Search accordions by keyword"
                value={searchKeyword}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className="input-bs-search"
                    >
                      <SearchIcon sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#d1d5db",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9ca3af",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#D31710",
                    },
                  },
                }}
              />
              <FormControl
                variant="outlined"
                style={{ width: "20%" }}
                sx={{ minWidth: 100 }}
              >
                <InputLabel>Sort by Result Count</InputLabel>
                <Select
                  value={sortOrder}
                  onChange={handleSortOrderChange}
                  label="Sort by Result Count"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="asc">Asc</MenuItem>
                  <MenuItem value="desc">Desc</MenuItem>
                </Select>
              </FormControl>
              <CustomButton
                variant="outlined"
                size="small"
                style={{ padding: "14px" }}
                startIcon={
                  !exportLoading && <DownloadIcon sx={{ fontSize: "1rem" }} />
                }
                onClick={handleExportAll}
                disabled={
                  filterResults.length === 0 ||
                  (continuePolling && pollingTimeRemaining !== null)
                }
                sx={{
                  px: 1,
                  py: 0.5,
                  backgroundColor: "white",
                  fontSize: "0.875rem",
                  textTransform: "none",
                }}
              >
                {exportLoading ? (
                  <CircularProgress sx={{ color: "#ce2a1d" }} size={64} />
                ) : (
                  "Export Results"
                )}
              </CustomButton>
            </Box>

            <div className="my-2">
              <Stack spacing={3}>
                {/* Found in EPH Accordion */}
                {sortResults(filteredResults)
                  .slice(
                    accordionPage * accordionRowsPerPage,
                    accordionPage * accordionRowsPerPage + accordionRowsPerPage,
                  )
                  .map((result, index) => {
                    // Create unique key for each accordion's pagination state
                    const accordionKey = `accordion_${accordionPage * accordionRowsPerPage + index}`;
                    if (!pageStates[accordionKey]) {
                      setPageStates((prev) => ({
                        ...prev,
                        [accordionKey]: { page: 0, rowsPerPage: 5 },
                      }));
                    }
                    const currentPageState = pageStates[accordionKey] || {
                      page: 0,
                      rowsPerPage: 5,
                    };

                    return (
                      <Accordion
                        key={index}
                        className={
                          index === 0
                            ? "accordion-style-bs"
                            : "accordion-style-bs accordian-mt-bs"
                        }
                        expanded={
                          (result.results.length > 0 &&
                            expandedAccordions[accordionKey]) ||
                          false
                        }
                        onChange={handleAccordionExpand(
                          accordionKey,
                          result.results.length > 0,
                        )}
                        sx={
                          result.results.length === 0
                            ? {
                                backgroundColor: "#fef3f2",
                                border: "1px solid #f87171",
                                "& .MuiAccordionSummary-root": {
                                  cursor: "default !important",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                  },
                                },
                              }
                            : {}
                        }
                      >
                        <AccordionSummary
                          className="accordian-title-background-bs"
                          expandIcon={
                            result.results.length > 0 ? (
                              <ExpandMoreIcon />
                            ) : null
                          }
                        >
                          <Box
                            display="flex"
                            className="accordian-margin-bs"
                            alignItems="center"
                            gap={2}
                          >
                            <Typography
                              variant="h6"
                              className="fw-bold _wg-dynamic-text accordian-title-bs"
                              sx={{
                                maxWidth: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={Object.entries(result.input[0] || {})
                                .filter(([key, value]) => value)
                                .map(([key, value]) => {
                                  const formattedKey = key
                                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                                    .replace(/[_-]/g, " ")
                                    .replace(/\s+/g, " ")
                                    .trim()
                                    .split(" ")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1).toLowerCase(),
                                    )
                                    .join(" ");
                                  return `${formattedKey}: ${value}`;
                                })
                                .join(", ")}
                            >
                              {Object.entries(result.input[0] || {})
                                .filter(([key, value]) => value)
                                .map(([key, value]) => {
                                  const formattedKey = key
                                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                                    .replace(/[_-]/g, " ")
                                    .replace(/\s+/g, " ")
                                    .trim()
                                    .split(" ")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1).toLowerCase(),
                                    )
                                    .join(" ");
                                  return `${formattedKey}: ${value}`;
                                })
                                .join(", ")}
                            </Typography>
                            <Chip
                              label={result.results.length}
                              color={
                                result.results.length === 0 ? "error" : "error"
                              }
                              size="small"
                            />
                            <span style={{ fontWeight: "400" }}>
                              of {result.count}
                            </span>
                            {result.results.length === 0 && (
                              <Chip
                                label="No Match"
                                color="error"
                                variant="outlined"
                                size="small"
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails
                          className="table-background-bs"
                          sx={{
                            maxHeight: "500px",
                            overflowY: "auto",
                            padding: "16px",
                          }}
                        >
                          {result.results.length === 0 ? (
                            <Box
                              sx={{
                                textAlign: "center",

                                color: "#6b7280",
                              }}
                            >
                              <Typography variant="h6">
                                No matching results found
                              </Typography>
                            </Box>
                          ) : (
                            <ResultsTable
                              tableData={result.results}
                              headers={headers}
                              filters={filters}
                              sortableColumns={[
                                "HCPUniqueId",
                                "vendorUniqueID",
                              ]}
                              handleDownload={handleDownload}
                              tableType="bulk-search"
                              continuePolling={continuePolling}
                              currentPageState={currentPageState}
                              accordionKey={accordionKey}
                              handleChangePage={handleChangePage}
                              handleChangeRowsPerPage={handleChangeRowsPerPage}
                            />
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}

                {/* No Results Found Message */}
                {filteredResults.length === 0 && searchKeyword.trim() && (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "#6b7280",
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No results found
                    </Typography>
                    <Typography variant="body2">
                      Try searching with different keywords or clear the search
                      to view all results.
                    </Typography>
                  </Box>
                )}

                {/* Pagination for Accordions */}
                {filteredResults.length > accordionRowsPerPage && (
                  <Box>
                    <TablePagination
                      count={filteredResults.length}
                      page={accordionPage}
                      onPageChange={handleAccordionPageChange}
                      rowsPerPage={accordionRowsPerPage}
                      onRowsPerPageChange={handleAccordionRowsPerPageChange}
                      rowsPerPageOptions={[10, 20, 30, 40, 50]}
                      ActionsComponent={TablePaginationActions}
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} of ${count} Results`
                      }
                      labelRowsPerPage="Rows per page:"
                      sx={{
                        display: "flex",
                        justifyContent: "end",
                        "& .MuiTablePagination-toolbar": {
                          display: "flex",
                          justifyContent: "end",
                          alignItems: "center",
                          px: 2,
                          py: 1,
                        },
                        "& .MuiTablePagination-displayedRows": {
                          margin: 0,
                          fontSize: "0.875rem",
                          color: "#374151",
                        },
                        "& .MuiTablePagination-selectLabel": {
                          margin: 0,
                          fontSize: "0.875rem",
                          color: "#374151",
                        },
                        "& .MuiTablePagination-select": {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  </Box>
                )}
                <BackButton buttonIcon="no" backUrl="/bulk-search" />
              </Stack>
            </div>
          </Box>
        ) : (
          <Box className="mx-5 my-4">
            <ResultCards
              totalRecords={results.length}
              searchType={searchType}
              totalFound={
                results.filter(
                  (r) =>
                    r.Source === "Found in EPH" ||
                    r.Source === "Found in Vendor",
                ).length
              }
              totalNotFound={
                results.filter((r) => r.Source === "Not Found").length
              }
              totalFailed={
                results.length < parseInt(totalResults) && !continuePolling
                  ? parseInt(totalResults) - results.length
                  : 0
              }
            />
            <Box>
              {totalResults && results.length > 0 && (
                <Box className="bs-progress-bar-pad">
                  <Box
                    sx={{
                      position: "relative",
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: "#e9ecef",
                      overflow: "hidden",
                    }}
                  >
                    {/* Green section for processed records */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: `${Math.min((results.length / parseInt(totalResults)) * 100, 100)}%`,
                        backgroundColor: "#16A34A", // Green for processed records
                        borderRadius: 5,
                        transition: "width 0.3s ease-in-out",
                      }}
                    />
                    {/* Red section for remaining records (only shown when processing is complete) */}
                    {!continuePolling &&
                      results.length < parseInt(totalResults) && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: `${Math.min((results.length / parseInt(totalResults)) * 100, 100)}%`,
                            top: 0,
                            height: "100%",
                            width: `${100 - Math.min((results.length / parseInt(totalResults)) * 100, 100)}%`,
                            backgroundColor: "#DC2626", // Red for remaining records
                            borderRadius: 5,
                            transition: "width 0.3s ease-in-out",
                          }}
                        />
                      )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                      {results.length} of {totalResults} records processed
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: continuePolling ? "#007bff" : "#28a745",
                        fontWeight: 600,
                      }}
                    >
                      {continuePolling ? (
                        <Typography variant="body2" sx={{ color: "#6c757d" }}>
                          {Math.round(
                            (results.length / parseInt(totalResults)) * 100,
                          )}
                          %
                        </Typography>
                      ) : (
                        <span>Complete</span>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <div className="my-2">
              <Stack spacing={3}>
                <ResultsTable
                  tableData={results}
                  headers={headers}
                  filters={filters}
                  sortableColumns={["HCPUniqueId", "vendorUniqueID"]}
                  handleDownload={handleDownload}
                  tableType="bulk-search"
                  continuePolling={continuePolling}
                />
                <BackButton buttonIcon="no" backUrl="/bulk-search" />
              </Stack>
            </div>
          </Box>
        )}
      </Card>
      {downloadModalOpen && (
        <DownloadVendor
          open={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          handleConfirm={handleConfirm}
          modalTextType={textType}
          oneKeyId={oneKeyId}
          vendorResponse={vendorResponse}
          recordType={recordType}
        />
      )}
    </div>
  );
};
export default SearchResults;
